import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { QAQC_METRIC_CATEGORY_LABELS, type QaqcMetricCategory } from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { useCan } from '../../lib/session.js';
import { DerivedFigure, StatusChip, VarianceBadge } from '../../components/qaqc/DerivedFigure.js';
import { PqiCard } from '../../components/qaqc/PqiCard.js';
import { QualityBriefPanel } from '../../components/qaqc/QualityBriefPanel.js';
import { CustomerSatisfaction } from '../../components/qaqc/CustomerSatisfaction.js';
import { ImportWorkbook } from '../../components/qaqc/ImportWorkbook.js';
import {
  ApiError,
  approvePeriod,
  downloadPeriodExport,
  fetchPeriod,
  saveMaterialInspection,
  saveMetrics,
  saveQtbt,
  sendBackPeriod,
  submitPeriod,
  type MetricEntry,
} from '../../lib/api.js';
import { t } from '../../lib/i18n.js';

/**
 * The monthly entry workspace (spec §9).
 *
 * Every figure the user types is an input. Every figure derived from them is
 * shown read-only, straight from the server's response — the screen never
 * computes a number the API has not already stored, so what is displayed and what
 * is recorded cannot drift apart (ADR-0007).
 */

const NUMBER_FIELDS: { key: keyof MetricEntry; label: string }[] = [
  { key: 'issuedCount', label: 'Issued' },
  { key: 'closedCount', label: 'Closed' },
  { key: 'ageing0To15', label: '0–15 days' },
  { key: 'ageing15To45', label: '15–45 days' },
  { key: 'ageingOver45', label: 'Over 45 days' },
];

export function ReportPeriodScreen() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<{ section: string; message: string }[]>([]);
  const [remarks, setRemarks] = useState('');

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['qaqc-period', id],
    queryFn: () => fetchPeriod(id!),
    enabled: Boolean(id),
  });
  // Offered only to those who hold them somewhere; the server still decides (pass 9).
  const canEdit = useCan('qaqc', 'create_edit');
  const canSubmit = useCan('qaqc', 'submit');
  const canApprove = useCan('qaqc', 'approve_reject');
  const canExport = useCan('qaqc', 'export');

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['qaqc-period', id] });

  const run = <T,>(work: () => Promise<T>) => async () => {
    setError(null);
    setBlockers([]);
    try {
      await work();
      refresh();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        const details = caught.details as { blockers?: { section: string; message: string }[] } | undefined;
        if (details?.blockers) setBlockers(details.blockers);
      } else {
        setError(t("Something went wrong."));
      }
    }
  };

  const metricMutation = useMutation({
    mutationFn: (entry: MetricEntry) =>
      saveMetrics(id!, {
        category: entry.category,
        issuedCount: entry.issuedCount,
        closedCount: entry.closedCount,
        ageing0To15: entry.ageing0To15,
        ageing15To45: entry.ageing15To45,
        ageingOver45: entry.ageingOver45,
      }),
    onSuccess: refresh,
    // A save that fails says so, rather than leaving the old figure on screen.
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("Those figures could not be saved.")),
  });

  if (loadError) {
    return (
      <div className="min-h-full">
        <AppTopBar app="qaqc" />
        <div role="alert" className="mx-auto max-w-7xl px-4 py-8">
          <p className="m-0 font-semibold">{t("This report could not be opened.")}</p>
          <p className="mt-1 text-sm text-content-secondary">
            {loadError instanceof ApiError ? loadError.message : t("Check your connection and try again.")}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="min-h-full">
        <AppTopBar app="qaqc" />
        <p className="mx-auto max-w-7xl px-4 py-8 text-content-secondary">{t("Loading…")}</p>
      </div>
    );
  }

  const { period, metrics, materialInspection, qtbt, brief, customerSatisfaction } = data;
  const open = period.status === 'draft' || period.status === 'sent_back';
  const editable = open && canEdit;

  return (
    <div className="min-h-full">
      <AppTopBar app="qaqc" />
      <Breadcrumbs app="qaqc" trail={['Monthly report', period.periodStart.slice(0, 7)]} />

      <main className="mx-auto max-w-7xl px-4 pb-12 pt-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="m-0 text-2xl font-semibold">
              {t("Monthly QA/QC Report —")}{' '}{period.periodStart.slice(0, 7)}
            </h1>
            <div className="mt-2 flex items-center gap-2">
              <StatusChip status={period.status} />
              {!open && (
                <span className="text-sm text-content-secondary">
                  {t("Entry is closed while the report is")}{' '}{period.status.replace('_', ' ')}.
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {canExport && (
              <button
                type="button"
                onClick={run(() => downloadPeriodExport(period.id))}
                className="rounded-sm border border-border-strong px-3 py-2 text-sm font-medium"
              >
                {t("Export to Excel")}
              </button>
            )}
            {open && canSubmit && (
              <button
                type="button"
                onClick={run(() => submitPeriod(period.id))}
                className="rounded-sm bg-ag-purple px-4 py-2 text-sm font-medium text-content-inverse"
              >
                {t("Submit for approval")}
              </button>
            )}
          </div>
        </header>

        {period.sentBackRemarks && period.status === 'sent_back' && (
          <p role="alert" className="mt-4 rounded-sm bg-tone-pending-surface px-4 py-3 text-sm">
            <strong className="font-semibold">{t("Sent back:")}</strong> {period.sentBackRemarks}
          </p>
        )}

        {error && (
          <div role="alert" className="mt-4 rounded-sm bg-tone-critical-surface px-4 py-3 text-sm text-tone-critical">
            <p className="m-0 font-semibold">{error}</p>
            {/* Every blocker at once — fixing a monthly report should not mean
                submitting five times to discover five problems. */}
            {blockers.length > 0 && (
              <ul className="mb-0 mt-2 ps-5">
                {blockers.map((blocker) => (
                  <li key={`${blocker.section}-${blocker.message}`}>
                    <strong>{blocker.section}:</strong> {blocker.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-6">
          <PqiCard period={period} />
        </div>

        {editable && <ImportWorkbook periodId={period.id} onApplied={refresh} />}

        {/* --- Metrics --------------------------------------------------- */}
        <section className="mt-6 rounded-md border border-border bg-surface p-5 shadow-card">
          <h2 className="m-0 text-base font-semibold">{t("QA/QC Metrics")}</h2>
          <p className="mt-1 text-sm text-content-secondary">
            {t("Counts are entered. Closure rate and variance are calculated and cannot be edited.")}
          </p>

          <div className="mt-4 space-y-4">
            {metrics.map((entry) => (
              <MetricRow
                key={entry.id}
                entry={entry}
                editable={editable}
                onSave={(next) => metricMutation.mutate(next)}
              />
            ))}
          </div>
        </section>

        {/* --- Material inspection + QTBT -------------------------------- */}
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <MaterialInspectionCard
            periodId={period.id}
            entry={materialInspection}
            editable={editable}
            onSaved={refresh}
          />
          <QtbtCard periodId={period.id} entry={qtbt} editable={editable} onSaved={refresh} />
        </div>

        {/* --- Customer satisfaction ------------------------------------- */}
        <div className="mt-6">
          <CustomerSatisfaction
            periodId={period.id}
            responses={customerSatisfaction}
            editable={editable}
            onChanged={refresh}
          />
        </div>

        {/* --- Brief ------------------------------------------------------ */}
        <div className="mt-6">
          <QualityBriefPanel
            periodId={period.id}
            brief={brief}
            readOnly={!editable}
            onSaved={refresh}
          />
        </div>

        {/* --- Approval --------------------------------------------------- */}
        {period.status === 'submitted' && canApprove && (
          <section className="mt-6 rounded-md border border-border bg-surface p-5 shadow-card">
            <h2 className="m-0 text-base font-semibold">{t("Approval")}</h2>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div className="min-w-64 flex-1">
                <label htmlFor="send-back-remarks" className="text-sm font-medium">
                  {t("Remarks (required to send back)")}
                </label>
                <input
                  id="send-back-remarks"
                  value={remarks}
                  onChange={(event) => setRemarks(event.target.value)}
                  className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={run(() => sendBackPeriod(period.id, remarks))}
                className="rounded-sm border border-border-strong px-4 py-2 text-sm font-medium"
              >
                {t("Send back")}
              </button>
              <button
                type="button"
                onClick={run(() => approvePeriod(period.id))}
                className="rounded-sm bg-ag-purple px-4 py-2 text-sm font-medium text-content-inverse"
              >
                {t("Approve")}
              </button>
            </div>
            <p className="mt-2 text-xs text-content-secondary">
              {t("A report cannot be approved by the person who submitted it.")}
            </p>
          </section>
        )}
      </main>
    </div>
  );
}

function MetricRow({
  entry,
  editable,
  onSave,
}: {
  entry: MetricEntry;
  editable: boolean;
  onSave: (entry: MetricEntry) => void;
}) {
  const [draft, setDraft] = useState(entry);

  return (
    <div className="rounded-sm border border-border p-4">
      <h3 className="m-0 text-sm font-semibold">
        {t(QAQC_METRIC_CATEGORY_LABELS[entry.category as QaqcMetricCategory])}
      </h3>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {NUMBER_FIELDS.map((field) => (
          <div key={String(field.key)}>
            <label
              htmlFor={`${entry.id}-${String(field.key)}`}
              className="text-xs font-medium text-content-secondary"
            >
              {t(field.label)}
            </label>
            <input
              id={`${entry.id}-${String(field.key)}`}
              type="number"
              min={0}
              disabled={!editable}
              value={draft[field.key] as number}
              onChange={(event) =>
                setDraft({ ...draft, [field.key]: Number(event.target.value) || 0 })
              }
              className="mt-1 w-full rounded-sm border border-border-strong px-2 py-1.5 text-sm disabled:bg-surface-sunken"
            />
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4 border-t border-border pt-3">
        <dl className="flex flex-wrap gap-8">
          {/* Read-only, straight from the server (ADR-0007). */}
          <DerivedFigure
            label={t("Closure rate")}
            value={entry.closureRate}
            suffix="%"
            explanation={entry.closureRateExplanation}
          />
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-content-secondary">
              {t("Month over month")}
            </dt>
            <dd className="m-0 mt-1">
              <VarianceBadge value={entry.closureRateVariance} />
            </dd>
          </div>
        </dl>

        {editable && (
          <button
            type="button"
            onClick={() => onSave(draft)}
            className="rounded-sm border border-ag-purple px-3 py-1.5 text-sm font-medium text-ag-purple"
          >
            {t("Save")}
          </button>
        )}
      </div>
    </div>
  );
}

function MaterialInspectionCard({
  periodId,
  entry,
  editable,
  onSaved,
}: {
  periodId: string;
  entry: import('../../lib/api.js').MaterialInspection | null;
  editable: boolean;
  onSaved: () => void;
}) {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    mirnTotal: entry?.mirnTotal ?? 0,
    osdOverage: entry?.osdOverage ?? 0,
    osdShortage: entry?.osdShortage ?? 0,
    osdDamage: entry?.osdDamage ?? 0,
    statusApproved: entry?.statusApproved ?? 0,
    statusOnHold: entry?.statusOnHold ?? 0,
    statusRejected: entry?.statusRejected ?? 0,
    statusHazardous: entry?.statusHazardous ?? 0,
    statusHandleWithCare: entry?.statusHandleWithCare ?? 0,
  });

  const statusTotal =
    draft.statusApproved +
    draft.statusOnHold +
    draft.statusRejected +
    draft.statusHazardous +
    draft.statusHandleWithCare;
  const reconciles = statusTotal === draft.mirnTotal;

  const fields: { key: keyof typeof draft; label: string }[] = [
    { key: 'mirnTotal', label: 'MIRN total' },
    { key: 'osdOverage', label: 'Overage' },
    { key: 'osdShortage', label: 'Shortage' },
    { key: 'osdDamage', label: 'Damage' },
    { key: 'statusApproved', label: 'Approved' },
    { key: 'statusOnHold', label: 'On hold' },
    { key: 'statusRejected', label: 'Rejected' },
    { key: 'statusHazardous', label: 'Hazardous' },
    { key: 'statusHandleWithCare', label: 'Handle with care' },
  ];

  return (
    <section className="rounded-md border border-border bg-surface p-5 shadow-card">
      <h2 className="m-0 text-base font-semibold">{t("Material Inspection")}</h2>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {fields.map((field) => (
          <div key={field.key}>
            <label htmlFor={`mi-${field.key}`} className="text-xs font-medium text-content-secondary">
              {t(field.label)}
            </label>
            <input
              id={`mi-${field.key}`}
              type="number"
              min={0}
              disabled={!editable}
              value={draft[field.key]}
              onChange={(event) => setDraft({ ...draft, [field.key]: Number(event.target.value) || 0 })}
              className="mt-1 w-full rounded-sm border border-border-strong px-2 py-1.5 text-sm disabled:bg-surface-sunken"
            />
          </div>
        ))}
      </div>

      {/* Shown live so the problem is obvious while typing, but it only BLOCKS at
          submit — a half-entered draft must stay savable. */}
      {/* Announced as it changes, not only seen (WCAG 4.1.3; pass 11). */}
      <p
        role="status"
        className={`mt-3 rounded-sm px-3 py-2 text-xs ${
          reconciles ? 'bg-tone-positive-surface text-tone-positive-text' : 'bg-tone-pending-surface text-content-secondary'
        }`}
      >
        {reconciles
          ? t("Status breakdown reconciles to the MIRN total ({0}).", { 0: statusTotal })
          : t("Status breakdown totals {0} against a MIRN total of {1}. This must match before the report can be submitted.", { 0: statusTotal, 1: draft.mirnTotal })}
      </p>

      {editable && (
        <button
          type="button"
          onClick={async () => {
            setSaveError(null);
            try {
              await saveMaterialInspection(periodId, draft);
              onSaved();
            } catch (caught) {
              setSaveError(caught instanceof ApiError ? caught.message : t("The material inspection could not be saved."));
            }
          }}
          className="mt-3 rounded-sm border border-ag-purple px-3 py-1.5 text-sm font-medium text-ag-purple"
        >
          {t("Save")}
        </button>
      )}
      {saveError && (
        <p role="alert" className="mb-0 mt-2 text-sm text-tone-critical">
          {saveError}
        </p>
      )}
    </section>
  );
}

function QtbtCard({
  periodId,
  entry,
  editable,
  onSaved,
}: {
  periodId: string;
  entry: import('../../lib/api.js').QtbtEntry | null;
  editable: boolean;
  onSaved: () => void;
}) {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    sessionCount: entry?.sessionCount ?? 0,
    attendeeCount: entry?.attendeeCount ?? 0,
    totalDurationMinutes: entry?.totalDurationMinutes ?? 0,
  });

  const fields: { key: keyof typeof draft; label: string }[] = [
    { key: 'sessionCount', label: 'Sessions' },
    { key: 'attendeeCount', label: 'Attendees' },
    { key: 'totalDurationMinutes', label: 'Duration (minutes)' },
  ];

  return (
    <section className="rounded-md border border-border bg-surface p-5 shadow-card">
      <h2 className="m-0 text-base font-semibold">{t("Quality Toolbox Talks")}</h2>

      <div className="mt-3 grid grid-cols-3 gap-3">
        {fields.map((field) => (
          <div key={field.key}>
            <label htmlFor={`qtbt-${field.key}`} className="text-xs font-medium text-content-secondary">
              {t(field.label)}
            </label>
            <input
              id={`qtbt-${field.key}`}
              type="number"
              min={0}
              disabled={!editable}
              value={draft[field.key]}
              onChange={(event) => setDraft({ ...draft, [field.key]: Number(event.target.value) || 0 })}
              className="mt-1 w-full rounded-sm border border-border-strong px-2 py-1.5 text-sm disabled:bg-surface-sunken"
            />
          </div>
        ))}
      </div>

      <dl className="mt-4 border-t border-border pt-3">
        <DerivedFigure
          label={t("Average attendance")}
          value={entry?.averageAttendance ?? null}
          explanation={entry?.averageAttendanceExplanation}
        />
      </dl>

      {editable && (
        <button
          type="button"
          onClick={async () => {
            setSaveError(null);
            try {
              await saveQtbt(periodId, draft);
              onSaved();
            } catch (caught) {
              setSaveError(caught instanceof ApiError ? caught.message : t("The toolbox talks could not be saved."));
            }
          }}
          className="mt-3 rounded-sm border border-ag-purple px-3 py-1.5 text-sm font-medium text-ag-purple"
        >
          {t("Save")}
        </button>
      )}
      {saveError && (
        <p role="alert" className="mb-0 mt-2 text-sm text-tone-critical">
          {saveError}
        </p>
      )}
    </section>
  );
}
